import type { ProspectSortColumn, SortDirection, TableSort } from "../types/sort";
import { ariaSortFor } from "../types/sort";
import "./SortableHeaderCell.css";

interface SortableHeaderCellProps {
  /** Column key sent to the API as `sortBy`. */
  column: ProspectSortColumn;
  label: string;
  sort: TableSort;
  onSort: (column: ProspectSortColumn) => void;
  /**
   * False while a mode that owns row order is active (e.g. drag-to-reorder).
   * The header stays focusable and keeps its `aria-sort`, but does nothing.
   */
  sortable?: boolean;
  /** Class for the `<th>`; the host table supplies its own header styling. */
  className?: string;
}

const SortArrow = ({ direction }: { direction: SortDirection }) => (
  <svg
    className={`sortable-header__arrow sortable-header__arrow--${direction}`}
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M0 0h10L5 6z" />
  </svg>
);

/**
 * A `<th>` whose label toggles sorting for its column.
 *
 * The arrow renders only on the active column — a placeholder on every header
 * makes it much harder to see at a glance which column the table is sorted by.
 * `aria-sort` carries the same fact for screen readers, so the button's
 * accessible name stays the plain label: duplicating the state into the name
 * would have the active header announce "Name, sorted ascending … ascending",
 * and a name that changes on every click is a moving target for voice control.
 */
export const SortableHeaderCell = ({
  column,
  label,
  sort,
  onSort,
  sortable = true,
  className,
}: SortableHeaderCellProps) => {
  const isActive = sort.column === column;

  return (
    <th
      className={className}
      scope="col"
      // Set even when not sortable: if a sort is active, the table *is* sorted,
      // and a screen reader user needs to know that regardless of whether they
      // can currently change it.
      aria-sort={ariaSortFor(sort, column)}
    >
      <button
        type="button"
        className={`sortable-header__button${
          isActive ? " sortable-header__button--active" : ""
        }`}
        data-testid={`sort-${column}`}
        // aria-disabled rather than `disabled`, and never unmounted: a disabled
        // button drops out of the tab order, so a keyboard user mid-cycle on this
        // header would lose focus to the document body.
        aria-disabled={!sortable}
        onClick={sortable ? () => onSort(column) : undefined}
      >
        <span className="sortable-header__label">{label}</span>
        {isActive && <SortArrow direction={sort.direction} />}
      </button>
    </th>
  );
};
